import { Box, Card, Image, Typography, useMediaQueryContext } from "@drone-js/core";
import { type FunctionComponent, type MouseEvent, useEffect, useRef, useState } from "react";
import { PartnerMarketingLayout } from "./PartnerMarketingLayout";
import { usePartnerMarketingModelValue } from "./partnerMarketingModelAtom";

const CARD_TOP_PADDING = 100;
const BASE_URL = "https://cdn6.agoda.net/images/agoda-homes/partner-marketing";

const WhyAgodaImageMap: Record<string, string> = {
    card_1: "multilingual-support",
    card_2: "properties-worldwide",
    card_3: "data-rich-analytics",
    card_4: "manage-on-app",
};

export const WhyAgodaSection: FunctionComponent = () => {
    const {
        whyAgodaSectionViewModel: { whyAgodaTitle, whyAgodaCards },
    } = usePartnerMarketingModelValue();

    const [isAtStickyPosition, setIsAtStickyPosition] = useState<Record<number, boolean>>({});
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const mediaQuery = useMediaQueryContext();
    const CARD_TOP_OFFSET = mediaQuery.m ? 24 : 32;

    // Initialize Intersection Observer
    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const cardId = entry.target.id;
                    const cardIndex = parseInt(cardId.replace("card_", "")) - 1;

                    // Check if card is at sticky position (top of viewport)
                    const rect = entry.target.getBoundingClientRect();
                    const isSticky = rect.top <= CARD_TOP_PADDING + 10; // 10px tolerance

                    setIsAtStickyPosition((prev) => ({
                        ...prev,
                        [cardIndex]: isSticky,
                    }));
                });
            },
            {
                rootMargin: `-${CARD_TOP_PADDING}px 0px -50% 0px`,
                threshold: [0, 0.1, 0.5, 1.0],
            },
        );

        // Observe all cards
        cardRefs.current.forEach((card) => {
            if (card && observerRef.current) {
                observerRef.current.observe(card);
            }
        });

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [CARD_TOP_PADDING]);

    const scrollToCard = (targetIndex: number) => {
        const targetCard = cardRefs.current[targetIndex];
        if (targetCard) {
            const offsetTop = targetCard.offsetTop - CARD_TOP_PADDING;
            window.scrollTo({
                top: offsetTop,
                behavior: "smooth",
            });
        }
    };

    const CARD_BOUNDARIES: Record<string, Record<"start" | "end", number>> = {
        card_1: {
            start: 0,
            end: CARD_TOP_OFFSET - 1,
        },
        card_2: {
            start: CARD_TOP_OFFSET,
            end: 2 * CARD_TOP_OFFSET - 1,
        },
        card_3: {
            start: 2 * CARD_TOP_OFFSET,
            end: 3 * CARD_TOP_OFFSET - 1,
        },
    };

    function isInBoundaries(cardId: string, mouseYPos: number) {
        return mouseYPos >= CARD_BOUNDARIES[cardId].start && mouseYPos <= CARD_BOUNDARIES[cardId].end;
    }

    const handleCardClick = (currentIndex: number, e: MouseEvent) => {
        const currentCard = cardRefs.current[currentIndex];
        if (!currentCard) return;

        if (e.target === e.currentTarget) {
            // Click on the card itself
            const rect = currentCard.getBoundingClientRect();

            if (rect.top > CARD_TOP_PADDING + 1) {
                // Current card has not reached the top - scroll to it
                scrollToCard(currentIndex);
            } else {
                // Current card is at the top - handle clicks on stacked area
                const mouseYPos = e.clientY - rect.top;

                if (isInBoundaries("card_1", mouseYPos)) {
                    scrollToCard(0);
                } else if (isInBoundaries("card_2", mouseYPos)) {
                    scrollToCard(1);
                } else if (isInBoundaries("card_3", mouseYPos)) {
                    scrollToCard(2);
                }
            }
        } else {
            // Click on card content when card is not at sticky position
            const rect = currentCard.getBoundingClientRect();
            if (rect.top >= CARD_TOP_PADDING) {
                scrollToCard(currentIndex);
            }
        }
    };

    const handleMouseMove = (currentIndex: number, e: MouseEvent) => {
        const currentCard = cardRefs.current[currentIndex];
        if (!currentCard) return;

        const isAtStickyPos = isAtStickyPosition[currentIndex];

        if (e.currentTarget === e.target && isAtStickyPos) {
            document.body.style.cursor = "pointer";
        } else if (e.currentTarget !== e.target) {
            document.body.style.cursor = "default";
        } else {
            document.body.style.cursor = "default";
        }
    };

    return (
        <PartnerMarketingLayout
            py={mediaQuery.m ? "64" : "48"}
            col={8}
            backgroundColor="base.background"
            data-element-name="why-agoda-section"
        >
            <Box display="flex" flexDirection="column" alignItems="center" gap="32">
                <Typography variant="heading-2xl">{whyAgodaTitle}</Typography>
                <Box pb="32" width="100%" mb="64">
                    {whyAgodaCards.map((card, index) => (
                        <div
                            ref={(el) => (cardRefs.current[index] = el)}
                            style={{
                                top: CARD_TOP_PADDING,
                                paddingTop: index * CARD_TOP_OFFSET,
                                height: "416px",
                                position: "sticky",
                            }}
                            id={`card_${index + 1}`}
                            key={card.title}
                            onClick={(e) => {
                                handleCardClick(index, e);
                            }}
                            onMouseMove={(e) => {
                                handleMouseMove(index, e);
                            }}
                            onMouseLeave={() => {
                                document.body.style.cursor = "default";
                            }}
                            data-element-name="why-agoda-card"
                            data-why-agoda-card={index}
                        >
                            <Card width="100%" variant="filled" backgroundColor="base.surface">
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    gap="16"
                                    height="416px"
                                    flexDirection="column"
                                    px="16"
                                    borderWidth="1"
                                    borderStyle="solid"
                                    borderRadius="16"
                                    borderColor="base.subtle"
                                    py="32"
                                    qM={{
                                        flexDirection: "row",
                                        px: "64",
                                        py: "48",
                                    }}
                                >
                                    <Image
                                        src={`${BASE_URL}/why-agoda-section-v2/${WhyAgodaImageMap[`card_${index + 1}`]}.svg`}
                                        loading="lazy"
                                        alt={card.title}
                                        responsive="auto"
                                        width="295px"
                                        height="auto"
                                        qM={{
                                            width: "268px",
                                            height: "auto",
                                        }}
                                        qL={{
                                            width: "396px",
                                            height: "auto",
                                        }}
                                    />
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        flex={1}
                                        gap={mediaQuery.m ? "24" : "16"}
                                    >
                                        <Typography variant="heading-xl" textAlign={mediaQuery.m ? "start" : "center"}>
                                            {card.title}
                                        </Typography>
                                        <Typography
                                            variant="body-default"
                                            textColor="base.subtle"
                                            textAlign={mediaQuery.m ? "start" : "center"}
                                        >
                                            {card.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        </div>
                    ))}
                </Box>
            </Box>
        </PartnerMarketingLayout>
    );
};
